import api from "./api";

// E7 — AI consumption per client (dollars, UTC calendar month). Back-office only.

// Every client with its month spend, threshold, colour and end-of-month estimate.
export const getAiSpend = async () => {
  const response = await api.get(`/master/ai-spend`);
  return response.data;
};

// One client: spend by day, by user (userId 0 = system), by circuit, and the last 6 months.
export const getAiSpendDetail = async (key) => {
  const response = await api.get(`/master/ai-spend/${encodeURIComponent(key)}`);
  return response.data;
};

// « Débloquer jusqu'à la fin du mois »: the client is no longer blocked until the 1st of next month.
export const unlockAiSpend = async (key) => {
  const response = await api.post(`/master/ai-spend/${encodeURIComponent(key)}/unlock`);
  return response.data;
};

export const AI_SPEND_MIGRATION_MESSAGE = "Migration E7 à appliquer sur la base centrale.";

// The central base has not received the E7 migration yet: the API answers 409 with this code.
export const isAiSpendMigrationMissing = (error) =>
  error?.response?.status === 409 && error?.response?.data?.code === "AI_SPEND_MIGRATION_MISSING";

// Closed list of circuits (same order as the API).
export const AI_CIRCUIT_LABELS = {
  chat: "Chat",
  bpmn: "Processus",
  recherche: "Recherche",
  documents: "Documents",
  whatsapp: "WhatsApp",
  autre: "Autre",
};

export const aiCircuitLabel = (circuit) => AI_CIRCUIT_LABELS[circuit] || circuit;

// Colour of the month status -> Badge variant (theme tokens) and French label.
export const AI_COLOR_BADGES = {
  green: { variant: "success", label: "Vert" },
  orange: { variant: "warning", label: "Orange" },
  red: { variant: "danger", label: "Rouge" },
  watch: { variant: "info", label: "Veille" },
};

// Amounts in dollars with 2 decimals, French format: « 1 234,50 $ ».
export const formatUsd = (value) =>
  value == null || Number.isNaN(Number(value))
    ? "—"
    : `${Number(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;

export const formatPercent = (value) =>
  value == null || Number.isNaN(Number(value))
    ? "—"
    : // Rounded down: 99.6 % must not read « 100 % » while the client is still orange.
      `${Math.floor(Number(value)).toLocaleString("fr-FR")} %`;

// « 2026-10 » -> « octobre 2026 ».
export const aiMonthName = (month) =>
  month ? new Date(`${month}-01T00:00:00Z`).toLocaleDateString("fr-FR", { month: "long", year: "numeric", timeZone: "UTC" }) : "";

// Nightly check of our AI journal against the Azure bill: reason of an `error` day, in French.
export const AI_RECONCILIATION_REASONS = {
  "credentials-missing": "Accès Azure non configuré",
  currency: "Devise de facturation non convertible",
  http: "Azure injoignable",
};

// Gap between our journal and the Azure bill, signed: « +12,00 % », « −3,10 % ».
export const formatGapPercent = (value) =>
  value == null || Number.isNaN(Number(value))
    ? "—"
    : `${Number(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2, signDisplay: "exceptZero" })} %`;

// Threshold wording: empty = no threshold, 0 = AI cut for the month.
export const formatAiBudget = (budgetUsd) => (budgetUsd == null ? "Aucun" : formatUsd(budgetUsd));

// userId 0 is the system (jobs, process engine, no human user).
export const aiUserName = (user) => (Number(user.userId) === 0 ? "Système" : user.name || `Utilisateur ${user.userId}`);
